import {AxiosResponse} from 'axios';
import letrusAPI from 'APIs/letrusAPI/axiosInstance';
import {
  TrailCompletedData,
  EssayResultOrPending,
  TrailOutcomeId,
} from './types';
import {
  TRAIL_CLOSING_CONFIG as CFG,
  trailOutcomeEmoji,
  trailReinforcement,
} from './trailClosing';

// Real contract confirmed live against the CPAL-Trilha branch (letrus-api, Gustavo, 2026-09-21):
// `GET /api/v1/new_learning_path_user_instances/{id}/trail_summary/`, snake_case, totals already
// computed on the backend (vary by grade — e.g. 9th grade has a different question count — that's
// why the front no longer has its own fixed count, it just displays whatever comes in). Does not
// include reinforcement/emoji/fixed labels: those stay front-only (see trailClosing.ts).
interface TrailCompletedApiResponse {
  school_grade_label?: string;
  outcome?: {id: TrailOutcomeId; label: string};
  objectives?: boolean[];
  essays?: EssayResultOrPending[]; // null = the constructed-response-evaluator hasn't graded it yet
  points?: number;
  total_points?: number;
  objective_points?: number;
  objective_total?: number;
  essay_points?: number;
  essay_total?: number;
}

const TITLE_PREFIX = 'Trilha personalizada de leitura | ';

// Unlike simplified_feedback_grades/ (which can legitimately respond incomplete while grading
// hasn't finished), trail_summary/ is only called after the backend confirms the trilha is
// finished — so an incomplete payload here is a real anomaly, not an expected intermediate state.
// That's why it becomes an error (rejects the promise) instead of partial data promoted to the
// strong type — code review finding, 2026-09-22.
function isCompleteTrailCompletedResponse(
  response: TrailCompletedApiResponse,
): response is Required<TrailCompletedApiResponse> {
  return (
    typeof response.school_grade_label === 'string' &&
    response.outcome?.id != null &&
    response.outcome?.label != null &&
    Array.isArray(response.objectives) &&
    Array.isArray(response.essays) &&
    typeof response.points === 'number' &&
    typeof response.total_points === 'number' &&
    typeof response.objective_points === 'number' &&
    typeof response.objective_total === 'number' &&
    typeof response.essay_points === 'number' &&
    typeof response.essay_total === 'number'
  );
}

export function mapTrailCompletedResponse(
  response: TrailCompletedApiResponse,
): TrailCompletedData {
  if (!isCompleteTrailCompletedResponse(response)) {
    throw new Error('trail_summary respondeu um payload incompleto');
  }

  return {
    activityLabel: CFG.activityLabel,
    title: `${TITLE_PREFIX}${response.school_grade_label}`,
    resultLabel: CFG.resultLabel,
    outcome: {
      ...response.outcome,
      emoji: trailOutcomeEmoji(response.outcome.id),
    },
    reinforcement: trailReinforcement(response.outcome.id),
    objectives: response.objectives,
    essays: response.essays,
    points: response.points,
    totalPoints: response.total_points,
    objectivePoints: response.objective_points,
    objectiveTotal: response.objective_total,
    essayPoints: response.essay_points,
    essayTotal: response.essay_total,
    goodFeedback: CFG.goodFeedback,
    improveFeedback: CFG.improveFeedback,
    closingPhrase: CFG.closingPhrase,
  };
}

// learningPathUserInstanceId is the id of the LearningPathUserInstance (lpui) of the level trilha,
// not the id of the LearningPathTemplate/Activity — the front route isn't wired into the real
// navigation flow yet (nobody calls `navigate` here today, see
// useLearningPathNavigation.finishLearningPath), so whoever wires that connection needs to make
// sure the right id reaches here.
export const fetchTrailCompletedService = (
  learningPathUserInstanceId?: number | string,
): Promise<AxiosResponse<TrailCompletedData>> =>
  letrusAPI(
    `/api/v1/new_learning_path_user_instances/${learningPathUserInstanceId}/trail_summary/`,
  ).then((response: AxiosResponse<TrailCompletedApiResponse>) => ({
    ...response,
    data: mapTrailCompletedResponse(response.data),
  }));
